import {
  CANCELLATION_WINDOW_OPTIONS_MIN,
  LATE_ARRIVAL_GRACE_OPTIONS_MIN,
  MIN_BOOKING_NOTICE_OPTIONS_MIN,
  NO_SHOW_POLICY_VALUES,
  type CancellationWindowMinutes,
  type LateArrivalGraceMinutes,
  type MinBookingNoticeMinutes,
  type NoShowPolicy,
} from '@slikerr/shared';

/**
 * Value → i18n-label-key tables for the four structured booking policies
 * (#459), the web mirror of mobile's `src/business/policy-options.ts`.
 *
 * The VALUES are never re-listed here: each option list is built by mapping
 * over the `@slikerr/shared` array that the API's own zod `.refine` validates
 * against, so web can only ever offer a value the server accepts. The label
 * tables are typed `Record<<the shared union>, string>`, which makes a value
 * added to `@slikerr/shared` a compile error here rather than an option that
 * silently goes missing from the dashboard.
 *
 * The Hebrew and English strings behind these keys are copied VERBATIM from
 * mobile's `bizPage.*` labels (apps/mobile/src/i18n/locales/*.json). The same
 * question must not be worded two ways across surfaces, and the owner may well
 * set the policy on one surface and read it back on the other.
 *
 * `0` is a real answer in three of the four lists ("flexible", "no grace
 * period", "no minimum"), never an absence — see `PolicySelect` in the
 * settings page for the empty-string ↔ null handling that keeps it that way.
 */
export interface PolicyOption<T> {
  value: T;
  labelKey: string;
}

const CANCELLATION_LABEL_KEYS: Record<CancellationWindowMinutes, string> = {
  0: 'settings.policy_cancellation_flexible',
  60: 'settings.policy_cancellation_1h',
  120: 'settings.policy_cancellation_2h',
  360: 'settings.policy_cancellation_6h',
  720: 'settings.policy_cancellation_12h',
  1440: 'settings.policy_cancellation_24h',
  2880: 'settings.policy_cancellation_48h',
  4320: 'settings.policy_cancellation_72h',
};

const LATE_ARRIVAL_LABEL_KEYS: Record<LateArrivalGraceMinutes, string> = {
  0: 'settings.policy_late_arrival_none',
  5: 'settings.policy_late_arrival_5',
  10: 'settings.policy_late_arrival_10',
  15: 'settings.policy_late_arrival_15',
  20: 'settings.policy_late_arrival_20',
  30: 'settings.policy_late_arrival_30',
};

const NO_SHOW_LABEL_KEYS: Record<NoShowPolicy, string> = {
  no_penalty: 'settings.policy_no_show_no_penalty',
  may_restrict: 'settings.policy_no_show_may_restrict',
  fee_may_apply: 'settings.policy_no_show_fee_may_apply',
};

const MIN_NOTICE_LABEL_KEYS: Record<MinBookingNoticeMinutes, string> = {
  0: 'settings.policy_min_notice_none',
  60: 'settings.policy_min_notice_1h',
  120: 'settings.policy_min_notice_2h',
  360: 'settings.policy_min_notice_6h',
  1440: 'settings.policy_min_notice_24h',
};

export const CANCELLATION_WINDOW_OPTIONS: PolicyOption<number>[] =
  CANCELLATION_WINDOW_OPTIONS_MIN.map((value) => ({
    value,
    labelKey: CANCELLATION_LABEL_KEYS[value],
  }));

export const LATE_ARRIVAL_GRACE_OPTIONS: PolicyOption<number>[] =
  LATE_ARRIVAL_GRACE_OPTIONS_MIN.map((value) => ({
    value,
    labelKey: LATE_ARRIVAL_LABEL_KEYS[value],
  }));

export const NO_SHOW_POLICY_OPTIONS: PolicyOption<NoShowPolicy>[] = NO_SHOW_POLICY_VALUES.map(
  (value) => ({ value, labelKey: NO_SHOW_LABEL_KEYS[value] }),
);

export const MIN_BOOKING_NOTICE_OPTIONS: PolicyOption<number>[] =
  MIN_BOOKING_NOTICE_OPTIONS_MIN.map((value) => ({
    value,
    labelKey: MIN_NOTICE_LABEL_KEYS[value],
  }));
